import {
  DEFAULT_ECONOMIC_LIFE,
  DEFAULT_ECONOMIC_LIFE_MANUFACTURED,
  Improvements,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { CONDITION_TABLE_HEADERS } from '../../../../modules/commentary/consts.ts';
import {
  selectAppliedConditionAdjsDisplay,
  selectConditionAdjs,
} from '../adjustmentSelectors/selectConditionAdj.ts';
import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectCompTypes,
  selectDepreciationSettings,
} from '../orderSelectors.ts';
import { selectImprovementEntityIds, selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import {
  selectDepreciationRatiosForFeature,
  selectDepreciationRatiosForFeatureDisplay,
} from '../selectDepreciationRatioForFeature.ts';
import { selectDwellingCostNewsDisplay } from '../selectDwellingCostNew.ts';
import { getCommentaryTableHelperText } from '../utils/getCommentaryTableHelperText.ts';
import { isAivRow, isArvRow } from './utils.ts';

const getConditionRowLabel = (id: string, index: number) => {
  if (id === 'subject' || id === 'subjectArv') {
    return 'SUB';
  }
  return `CP ${index}`;
};

const getEffectiveAgeString = (
  effectiveAge: number | undefined,
  economicLife: number | undefined,
  defaultEconomicLife: number,
) => `${effectiveAge ?? '-'} / ${economicLife ?? defaultEconomicLife}`;

const calculateDifferenceInDepreciation = (
  subjectDepreciation: number | undefined,
  compDepreciation: number | undefined,
) => {
  if (isUndefined(subjectDepreciation) || isUndefined(compDepreciation)) {
    return undefined;
  }

  return subjectDepreciation - compDepreciation;
};

const getDepreciationDifferenceString = (depreciationDifference: number | string | undefined) => {
  if (isUndefined(depreciationDifference)) {
    return '-';
  }

  if (typeof depreciationDifference === 'string') {
    return depreciationDifference;
  }

  return `${(depreciationDifference * -100).toFixed(2)}%`;
};

const getConditionRow = (
  id: string,
  index: number,
  improvements: Improvements,
  depreciation: string | undefined,
  depreciationDifference: number | string | undefined,
  dwellingCostNewsDisplay: string | undefined,
  adjustment: string | undefined,
  appliedAdjustment: string | undefined,
  depreciationSettings: ReturnType<typeof selectDepreciationSettings>,
) => ({
  id,
  label: getConditionRowLabel(id, index),
  effAgeAndEconomicLife: getEffectiveAgeString(
    improvements.effectiveAge,
    improvements.totalEconomicLife,
    improvements.propertyType === 'manufacturedHome'
      ? (depreciationSettings.defaultEcoLifeManufactured ?? DEFAULT_ECONOMIC_LIFE_MANUFACTURED)
      : (depreciationSettings.defaultEcoLife ?? DEFAULT_ECONOMIC_LIFE),
  ),
  depreciation: depreciation ?? '-',
  depreciationDifference: getDepreciationDifferenceString(depreciationDifference),
  costNew: dwellingCostNewsDisplay ?? '-',
  rawAdjustment: adjustment ?? '-',
  appliedAdjustment: appliedAdjustment ?? '-',
});

const selectConditionCommentaryData = createSelector(
  [
    selectCompSequence,
    selectImprovementsEntities,
    selectDepreciationRatiosForFeatureDisplay,
    selectDepreciationRatiosForFeature,
    selectDwellingCostNewsDisplay,
    selectConditionAdjs,
    selectAppliedConditionAdjsDisplay,
    selectAdjustmentSettings,
    selectDepreciationSettings,
    selectManualAdjustmentsEntities,
    selectImprovementEntityIds,
    selectCompTypes,
  ],
  (
    compSequence,
    improvementsEntities,
    depreciationRatiosDisplay,
    depreciationRatios,
    dwellingCostNewsDisplay,
    conditionAdjs,
    appliedConditionAdjs,
    adjustmentSettings,
    depreciationSettings,
    manualAdjustmentsEntities,
    improvementEntityIds,
    compTypes,
  ) => ({
    helperText: getCommentaryTableHelperText(
      adjustmentSettings,
      'condition',
      undefined,
      manualAdjustmentsEntities,
    ),
    data: improvementEntityIds.sort((a, b) => {
      if (a === 'subjectArv' || a === 'subject') return -1;
      if (b === 'subjectArv' || b === 'subject') return 1;
      return 0;
    }).map((id) => {
      const improvements = improvementsEntities[id];
      const depreciation = depreciationRatiosDisplay?.[id];
      let depreciationDifference: number | string | undefined = '-';
      if (id !== 'subjectArv' && id !=='subject') {
        const isArv = compTypes[id] === 'arv';
        if (isArv) {
          depreciationDifference = calculateDifferenceInDepreciation(
            depreciationRatios?.subjectArv,
            depreciationRatios?.[id],
          )
        } else {
          depreciationDifference = calculateDifferenceInDepreciation(
            depreciationRatios?.subject,
            depreciationRatios?.[id],
          );
        }
      }

      if (isUndefined(improvements)) {
        throw new Error('error');
      }

      return getConditionRow(
        id,
        compSequence.indexOf(id) + 1,
        improvements,
        depreciation,
        depreciationDifference,
        dwellingCostNewsDisplay?.[id],
        (id === 'subject' || id === 'subjectArv') ? '-' : conditionAdjs?.[id]?.toString(true),
        (id === 'subject' || id === 'subjectArv') ? '-' : appliedConditionAdjs?.[id]?.value,
        depreciationSettings,
      );
    }),
  }),
);

export const selectConditionCommentaryTableDisplay = createSelector(
  [selectConditionCommentaryData, selectCompTypes],
  ({
    data,
    helperText,
  }, compTypes): {
    helperText?: string;
    data: [string[], ...string[][]];
  } => ({
    helperText,
    data: [
      CONDITION_TABLE_HEADERS,
      ...data.filter((row) => isAivRow(row, compTypes)).map(
        ({
          label,
          effAgeAndEconomicLife,
          depreciation,
          depreciationDifference,
          costNew,
          rawAdjustment,
          appliedAdjustment,
        }) => [
          label,
          effAgeAndEconomicLife,
          depreciation,
          depreciationDifference,
          costNew,
          rawAdjustment,
          appliedAdjustment,
        ],
      ),
    ],
  }),
);

export const selectArvConditionCommentaryTableDisplay = createSelector(
  [selectConditionCommentaryData, selectCompTypes],
  ({
    data,
    helperText,
  }, compTypes): {
    helperText?: string;
    data: [string[], ...string[][]];
  } => ({
    helperText,
    data: [
      CONDITION_TABLE_HEADERS,
      ...data.filter((row) => isArvRow(row, compTypes)).map(
        ({
          label,
          effAgeAndEconomicLife,
          depreciation,
          depreciationDifference,
          costNew,
          rawAdjustment,
          appliedAdjustment,
        }) => [
          label,
          effAgeAndEconomicLife,
          depreciation,
          depreciationDifference,
          costNew,
          rawAdjustment,
          appliedAdjustment,
        ],
      ),
    ],
  }),
);
